import React, { memo } from 'react';
import { useT } from 'client/i18n/hooks/useT';
import { MarketingVideo } from 'components/MarketingPagesShared/MarketingVideo';
import { MarketingImage } from 'components/MarketingPagesShared/MarketingImage';
import { MarketingLink } from 'components/MarketingPagesShared/styles';
import {
  FeaturesCaptionedImage,
  FeaturesSection,
  FeaturesSectionSubtitle,
  FeaturesSectionTitle,
  FeaturesHorizontalCaption,
  FeaturesSectionDescription,
} from '../../styles';

function FeaturesMonetizationSectionView() {
  const t = useT();
  return (
    <FeaturesSection>
      <FeaturesSectionTitle>
        {t(
          'FeaturesScreen.FeaturesMonetizationSection.Title',
          'Get paid to podcast',
          'Anchor features page monetization section title'
        )}
      </FeaturesSectionTitle>
      <FeaturesCaptionedImage>
        <MarketingVideo
          width={970}
          height={546}
          title={t(
            'FeaturesScreen.FeaturesMonetizationSection.SubscriptionsVideoTitle',
            'Podcast Subscriptions - From Anchor and Spotify',
            'Anchor features page, monetization section, subscriptions video title'
          )}
          youTubeVideoId="1VSniHXVb74"
        >
          <MarketingImage
            alt={t(
              'FeaturesScreen.FeaturesMonetizationSection.SubscriptionsImageAlt',
              'Collage showing a mouth, asound wave, a microphone and a recording signal',
              'Anchor features page, monetization section, subscriptions image screenreader information'
            )}
            imagePath="features/podcast-subscriptions"
            width={970}
            height={545}
          />
        </MarketingVideo>
        <FeaturesHorizontalCaption>
          <FeaturesSectionSubtitle>
            {t(
              'FeaturesScreen.FeaturesMonetizationSection.SubscriptionsTitle',
              'Podcast Subscriptions',
              'Anchor features page, monetization section, subscriptions feature title'
            )}
          </FeaturesSectionSubtitle>
          <FeaturesSectionDescription>
            {t(
              'FeaturesScreen.FeaturesMonetizationSection.SubscriptionsDescription',
              'The easiest way for creators to offer exclusive content. Creator-friendly features include a wide range of subscription pricing structures and the ability to email subscribers directly.',
              'Anchor features page, monetization section, subscriptions feature description'
            )}
          </FeaturesSectionDescription>
        </FeaturesHorizontalCaption>
      </FeaturesCaptionedImage>

      <FeaturesCaptionedImage>
        <MarketingImage
          alt={t(
            'FeaturesScreen.FeaturesMonetizationSection.AdsImageAlt',
            'A rendering of an Anchor modal showing a Record Ad button',
            'Anchor features page, monetization section, ads image screenreader information'
          )}
          imagePath="features/monetization-ads"
          width={970}
          height={545}
        />
        <FeaturesHorizontalCaption>
          <FeaturesSectionSubtitle>
            {t(
              'FeaturesScreen.FeaturesMonetizationSection.AdsTitle',
              'Ads, read by you',
              'Anchor features page, monetization section, ads feature title'
            )}
          </FeaturesSectionSubtitle>
          <FeaturesSectionDescription>
            {t(
              'FeaturesScreen.FeaturesMonetizationSection.AdsDescription',
              'Earn money every time people listen. With {marketingLink}, record audio ads in your own voice or insert sponsor-read ads, and choose where they go in your episodes.',
              'Anchor features page, monetization section, ads feature description',
              {
                marketingLink: (
                  <MarketingLink
                    to="/ads"
                    key="feature-screen__marketing-link-ads-by-anchor"
                  >
                    {t(
                      'FeaturesScreen.FeaturesMonetizationSection.AdsByAnchorLinkText',
                      'Ads by Anchor',
                      'Ads by Anchor link text'
                    )}
                  </MarketingLink>
                ),
              }
            )}
          </FeaturesSectionDescription>
        </FeaturesHorizontalCaption>
      </FeaturesCaptionedImage>
      <FeaturesCaptionedImage>
        <MarketingImage
          alt={t(
            'FeaturesScreen.FeaturesMonetizationSection.ListenerSupportImageAlt',
            'two people on a laptop recording a podcast, a couple sharing headphones while listening to a podcast, a person on the subway listening to a podcast',
            'Anchor features page, monetization section, listener support image screenreader information'
          )}
          imagePath="features/monetization-listener-support"
          width={970}
          height={545}
        />
        <FeaturesHorizontalCaption>
          <FeaturesSectionSubtitle>
            {t(
              'FeaturesScreen.FeaturesMonetizationSection.ListenerSupportTitle',
              'Listener support',
              'Anchor features page, monetization section, listener support feature title'
            )}
          </FeaturesSectionSubtitle>
          <FeaturesSectionDescription>
            {t(
              'FeaturesScreen.FeaturesMonetizationSection.ListenerSupportDescription',
              " An optional button on your Anchor profile lets listeners donate monthly—and they don't even need an Anchor account to chip in.",
              'Anchor features page, monetization section, listener support feature description'
            )}
          </FeaturesSectionDescription>
        </FeaturesHorizontalCaption>
      </FeaturesCaptionedImage>
    </FeaturesSection>
  );
}

export const FeaturesMonetizationSection = memo(
  FeaturesMonetizationSectionView
);
